// Return / exchange from an earlier bill, as the Fashion layout shows it. The
// rules are SuperMarket's (SMPurchaseReturnView): which orders offer a return,
// which lines can be picked, and how far a line's quantity may go. Core's
// useSalesReturn does the actual work (verifyReturn, the cart, DI-25 checks).

// Same condition SuperMarket uses to show "Sales Return" on an order row.
export function orderReturnable(order, posConfig) {
  return Boolean(order) && parseFloat(order.total) > 0 && posConfig?.showSalesReturn === "Y" && order.layAway === "N" && order.paid !== 0;
}

// A return of this order already parked on this till (only tracked locally
// when parked bills are not saved to the server).
export function returnAlreadyParked(order, parkedList, posConfig) {
  if (posConfig?.saveParkedBills === "Y") return false;
  return (parkedList || []).some((p) => p?.parkedOrderID === order?.sOrderID);
}

const isGiftCard = (row, giftCardItems) => (giftCardItems || []).some((g) => g?.mProductId === row?.product?.mProductId);

// Why a line can't be picked, or "" when it can.
// finalSale: product ids that were final sale on the bill's date (a Set).
export function lineBlockedReason(row, giftCardItems, finalSale) {
  if (!row) return "Not on this bill.";
  if (isGiftCard(row, giftCardItems)) return "Gift cards can't be returned here.";
  if (finalSale?.has?.(row.product?.mProductId)) return "Final sale — can't be returned.";
  if (Number(row.returnQty) >= Number(row.qty)) return "Already returned.";
  if (Number(row.remainingQty ?? row.qtyToReturn) <= 0) return "Already in a parked return.";
  return "";
}

// Most that can come back on this line: what's left after earlier and parked returns.
export const lineMaxQty = (row) => {
  const remaining = Number(row?.remainingQty);
  if (Number.isFinite(remaining)) return Math.max(0, remaining);
  return Math.max(0, Number(row?.qty || 0) - Number(row?.returnQty || 0));
};

// Typed qty → { qty } or { error }. Whole units unless the line was sold in part units.
export function parseReturnQty(value, row) {
  const text = String(value ?? "").trim();
  const n = Number(text);
  if (text === "" || !Number.isFinite(n) || n <= 0) return { error: "Enter a quantity above 0." };
  const partUnits = !Number.isInteger(Number(row?.qty));
  if (!partUnits && !Number.isInteger(n)) return { error: "Enter a whole number." };
  const max = lineMaxQty(row);
  if (n > max) return { error: `Only ${max} can be returned.` };
  return { qty: n };
}

// The rows with one line's qtyToReturn changed (a new array; rows are copied).
export const withReturnQty = (rows, key, qty) => rows.map((r) => (r.key === key ? { ...r, qtyToReturn: qty } : r));

// Gross value of the picked lines at their return quantities. Same unit price
// Core uses (linegross / qty, else grossstd), so this matches the refund.
export function returnValue(rows, keys) {
  const picked = new Set(keys || []);
  return (rows || []).reduce((sum, r) => {
    if (!picked.has(r.key) || !(Number(r.qtyToReturn) > 0)) return sum;
    const unit = r.linegross && r.qty ? Math.abs(r.linegross) / Math.abs(r.qty) : Number(r.grossstd) || 0;
    return sum + unit * Number(r.qtyToReturn);
  }, 0);
}

// What the customer pays (positive) or gets back (negative) in an exchange:
// the return cart total is already negative.
export const exchangeNet = (cart, exchangeCart) => (Number(exchangeCart?.total) || 0) + (Number(cart?.total) || 0);

// The bill is an exchange only once the return lines are on it: the flag is set
// as soon as Exchange is chosen and stays set after the bill is paid, so on its
// own it would show an empty "Coming back" list on the next sale. Same rule as
// SuperMarket's computeIsReturnExchange (SMCartTable).
export const isExchangeBill = (selectedReturnType, cart) => selectedReturnType === "EXCHANGE" && ((cart?.items?.length || 0) > 0 || cart?.isBlindReceipt === true);

// Picked lines with their return quantity above 0.
const pickedRows = (rows, keys) => {
  const picked = new Set(keys || []);
  return (rows || []).filter((r) => picked.has(r.key) && Number(r.qtyToReturn) > 0);
};

// Pieces coming back: the return quantities of the picked lines added up.
export const returnPieces = (rows, keys) => pickedRows(rows, keys).reduce((n, r) => n + Number(r.qtyToReturn), 0);

// Tax inside returnValue, from the bill's own line tax (linetax / qty, else
// unittax) — shown as "Includes GST", never added on top. rate is the line
// tax rate when every picked line shares one, else null.
export function returnTax(rows, keys) {
  const lines = pickedRows(rows, keys);
  const amount = lines.reduce((sum, r) => {
    const unit = r.linetax && r.qty ? Math.abs(r.linetax) / Math.abs(r.qty) : Math.abs(Number(r.unittax) || 0);
    return sum + unit * Number(r.qtyToReturn);
  }, 0);
  const rates = new Set(lines.map((r) => Number(r.tax?.rate)).filter((n) => Number.isFinite(n) && n > 0));
  return { amount, rate: rates.size === 1 ? [...rates][0] : null };
}

// Return window in days when every line on the bill has the same one, else null.
export function billReturnDays(rows) {
  const days = new Set((rows || []).map((r) => Number(r.product?.returnDays)).filter((n) => Number.isFinite(n) && n > 0));
  return days.size === 1 ? [...days][0] : null;
}

// What the till can give back, in Core's ReturnOptionsModal order and with its
// rules: no Refund when the bill was paid to the online store.
export function returnChoices(posConfig, refundBlocked) {
  const out = [];
  if (posConfig?.allowReturnRefund === "Y" && !refundBlocked) out.push({ type: "REFUND", label: "Refund" });
  if (posConfig?.allowReturnCreditNote === "Y") out.push({ type: "STORE_CREDIT", label: "Credit note" });
  if (posConfig?.allowReturnExchange === "Y") out.push({ type: "EXCHANGE", label: "Exchange" });
  return out;
}

// Sold under an offer or a discount: the line had a pricing rule or money off.
export const soldOnSale = (row) => Boolean(row?.pricingRule?.name || row?.pricingRule?.id) || Number(row?.discount) > 0 || Number(row?.discountValue) > 0 || Number(row?.totalDiscount) > 0;

// How one line may be settled: an altered piece can only be exchanged (it was
// cut to the customer), a sale piece goes to a credit note. null = any way.
// alteredIds: product ids with an alteration ticket on this bill (a Set).
export function lineSettleRule(row, alteredIds) {
  if (alteredIds?.has?.(row?.product?.mProductId)) return { only: ["EXCHANGE"], why: "Altered — exchange only" };
  if (soldOnSale(row)) return { only: ["STORE_CREDIT"], why: "Bought on sale — credit note only" };
  return null;
}

// The ways every picked line allows: { only: string[] | null, mixed: boolean }.
// mixed means the lines' rules leave nothing in common, so they have to come
// back separately.
export function settleRules(rows, keys, alteredIds) {
  let only = null;
  for (const r of pickedRows(rows, keys)) {
    const rule = lineSettleRule(r, alteredIds);
    if (!rule) continue;
    only = only ? only.filter((t) => rule.only.includes(t)) : [...rule.only];
  }
  return { only, mixed: Array.isArray(only) && only.length === 0 };
}

// Refunds above this need a manager. The tender is chosen later on the pay
// screen, so any refund this large could go out as cash: refundVia doesn't
// waive it.
export const CASH_REFUND_APPROVAL_ABOVE = 2000;
export const needsCashRefundApproval = (settleAs, refundVia, total) =>
  settleAs === "REFUND" && Math.abs(Number(total) || 0) > CASH_REFUND_APPROVAL_ABOVE;

// Days a credit note can be spent in.
export const CREDIT_NOTE_DAYS = 180;

// What an exchange comes to: the value coming back against the new items, and
// whether it is an even swap, the customer pays the difference, or gets it back.
export function exchangeDifference(cart, exchangeCart) {
  const back = Math.round(Math.abs(Number(cart?.total) || 0) * 100) / 100;
  const taking = Math.round((Number(exchangeCart?.total) || 0) * 100) / 100;
  const net = Math.round((taking - back) * 100) / 100;
  const kind = Math.abs(net) < 0.005 ? "even" : net > 0 ? "pay" : "refund";
  return { back, taking, amount: Math.abs(net), kind };
}

// Styles of the pieces coming back that come in other sizes, so an exchange
// can start from "same style, another size" before anything else.
export function sameStyleSwaps(items, styles) {
  const byVariant = new Map();
  for (const s of styles || []) for (const v of s.variants || []) if (v.product?.mProductId) byVariant.set(v.product.mProductId, s);
  const out = [];
  for (const it of items || []) {
    const style = byVariant.get(it?.productId);
    if (!style || (style.sizes || []).length < 2 || out.some((o) => o.style.styleId === style.styleId)) continue;
    const size = style.variants.find((v) => v.product?.mProductId === it.productId)?.size ?? null;
    out.push({ style, size });
  }
  return out;
}
